/**
 * Service for handling file-related API requests (upload, download info, etc.)
 * Uses authFetch for authenticated requests.
 */
import authService from './authService';
import config from '../config';

const FILE_API_PREFIX = '/api/v1/files';

/**
 * Uploads a file to the backend.
 * @param {File} file - The file object to upload.
 * @param {string} [category='resource'] - Optional category for the file.
 * @param {object} [appMetadata=null] - Optional application-specific metadata (as an object).
 * @param {boolean} [isPublic=false] - Whether the file should be public.
 * @returns {Promise<object>} - The response data from the backend (FileUploadResponse schema).
 * @throws {Error} If the upload fails or authentication is missing.
 */
export const uploadFile = async (
    file,
    category = 'resource',
    appMetadata = null,
    isPublic = false
) => {
    if (!file) {
        throw new Error('No file provided for upload.');
    }
    const url = `${config.API_BASE_URL}${FILE_API_PREFIX}/upload`;
    console.log(`Attempting file upload to: ${url}`);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_public', String(isPublic));
    if (category) {
        formData.append('category', category);
    }
    if (appMetadata) {
        try {
            formData.append('metadata_json', JSON.stringify(appMetadata));
        } catch (e) {
            console.error("Failed to stringify appMetadata:", e);
            throw new Error("Invalid application metadata provided.");
        }
    }
    try {
        const response = await authService.authFetch(url, {
            method: 'POST',
            body: formData,
        });
        if (!response.ok) {
            let errorDetail = `Upload failed with status: ${response.status}`;
            try {
                const errorData = await response.json();
                errorDetail = errorData.detail || errorDetail;
            } catch (e) {
            }
            throw new Error(errorDetail);
        }
        const result = await response.json();
        console.log("File upload successful via fileApi:", result);
        return result;
    } catch (error) {
        console.error('Error in fileApi.uploadFile:', error);
        authService.trackNetworkError('uploadFile', error);
        throw error;
    }
};

/**
 * Fetches metadata for a specific file.
 * @param {string} fileId - The ID of the file metadata record.
 * @returns {Promise<object>} - File metadata (FileInfoResponse schema).
 * @throws {Error} If fetching fails.
 */
export const getFileInfo = async (fileId) => {
    if (!fileId) throw new Error("File ID is required.");
    const url = `${config.API_BASE_URL}${FILE_API_PREFIX}/${fileId}/info`;
    console.log(`Fetching file info from: ${url}`);
    try {
        const response = await authService.authFetch(url);
        if (!response.ok) {
             let errorDetail = `Failed to fetch file info: ${response.status}`;
             if (response.status === 404) errorDetail = "File not found.";
             try { const errorData = await response.json(); errorDetail = errorData.detail || errorDetail; } catch(e) {}
             throw new Error(errorDetail);
        }
        return await response.json();
    } catch (error) {
        console.error(`Error fetching file info for ${fileId}:`, error);
        authService.trackNetworkError('getFileInfo', error);
        throw error;
    }
};

/**
 * Fetches temporary download URLs for multiple files.
 * @param {string[]} fileIds - An array of file metadata IDs.
 * @returns {Promise<object>} - Object mapping file IDs to URLs (FileUrlsResponse schema).
 * @throws {Error} If fetching fails.
 */
export const getFileUrls = async (fileIds) => {
    if (!Array.isArray(fileIds) || fileIds.length === 0) {
        return { urls: {} };
    }
    const url = `${config.API_BASE_URL}${FILE_API_PREFIX}/urls`;
    console.log(`Fetching URLs for ${fileIds.length} files...`);
    try {
        const response = await authService.authFetch(url, {
            method: 'POST',
            body: JSON.stringify({ file_ids: fileIds }),
        });
         if (!response.ok) {
             let errorDetail = `Failed to fetch file URLs: ${response.status}`;
             try { const errorData = await response.json(); errorDetail = errorData.detail || errorDetail; } catch(e) {}
             throw new Error(errorDetail);
         }
        return await response.json();
    } catch (error) {
        console.error('Error fetching file URLs:', error);
        authService.trackNetworkError('getFileUrls', error);
        throw error;
    }
};

/**
 * Downloads a file from the backend's secure endpoint for in-browser use (e.g., audio playback).
 * This version is optimized for getting a Blob.
 *
 * @param {string} fileId - The resource ID of the file to download.
 * @returns {Promise<Blob>} A promise that resolves to the file content as a Blob.
 * @throws {Error} If the API request fails.
 */
export const downloadFileAsBlob = async (fileId) => {
  if (!fileId) {
    throw new Error('File ID is required for download.');
  }
  const url = `${config.API_BASE_URL}${FILE_API_PREFIX}/${fileId}/download`;
  console.log(`[filesApi] Requesting file download for ID: ${fileId}`);
  try {
    const response = await authService.authFetch(url, {
      method: 'GET',
    });
    if (!response.ok) {
      let errorDetail = `Failed to download file: ${response.status}`;
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorDetail;
      } catch (e) {
        errorDetail = await response.text();
      }
      throw new Error(errorDetail);
    }
    const audioBlob = await response.blob();
    console.log(`[filesApi] Successfully downloaded file ${fileId} as a blob of size ${audioBlob.size}.`);
    return audioBlob;
  } catch (error) {
    console.error(`[filesApi] Error downloading file ${fileId}:`, error);
    authService.trackNetworkError('downloadFileAsBlob', error);
    throw error;
  }
};

/**
 * Downloads a file from the backend for saving to the user's device.
 * This version is optimized for triggering a browser download prompt.
 * @param {string} fileId - The ID of the file to download.
 * @param {string} [fallbackFilename='downloaded_file'] - A filename to use if not found in headers.
 * @returns {Promise<{blob: Blob, filename: string}>} - An object containing the file blob and filename.
 * @throws {Error} If the download fails.
 */
export const downloadFile = async (fileId, fallbackFilename = 'downloaded_file') => {
    if (!fileId) {
        throw new Error('File ID is required for download.');
    }
    const url = `${config.API_BASE_URL}${FILE_API_PREFIX}/${fileId}/download`;
    console.log(`[fileApi] Attempting file download from: ${url}`);
    try {
        const response = await authService.authFetch(url, {
            method: 'GET',
        });
        if (!response.ok) {
            let errorDetail = `Download failed with status: ${response.status}`;
            try {
                const errorData = await response.json();
                errorDetail = errorData.detail || errorDetail;
            } catch (e) {
                errorDetail = `${errorDetail} ${response.statusText || ''}`.trim();
            }
            throw new Error(errorDetail);
        }
        const contentDisposition = response.headers.get('content-disposition');
        let filename = fallbackFilename;
        if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename\*?=['"]?(?:UTF-\d'')?([^;\r\n"']+)['"]?;?/i);
            if (filenameMatch && filenameMatch[1]) {
                try {
                    filename = decodeURIComponent(filenameMatch[1]);
                } catch (e) {
                    const plainFilenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
                    if (plainFilenameMatch && plainFilenameMatch[1]) {
                        filename = plainFilenameMatch[1];
                    } else {
                        filename = filenameMatch[1];
                    }
                    console.warn("Error decoding filename from content-disposition, used fallback:", e, filenameMatch[1]);
                }
            } else {
                 const plainFilenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
                 if (plainFilenameMatch && plainFilenameMatch[1]) {
                    filename = plainFilenameMatch[1];
                 }
            }
        }
        const blob = await response.blob();
        console.log(`[fileApi] File downloaded successfully. Filename: ${filename}, Blob size: ${blob.size}`);
        return { blob, filename };
    } catch (error) {
        console.error('[fileApi] Error in downloadFile:', error);
        authService.trackNetworkError('downloadFile', error);
        throw error;
    }
};

export default {
    uploadFile,
    getFileInfo,
    getFileUrls,
    downloadFile,
    downloadFileAsBlob,
};